import Phaser from 'phaser';
import { PlayerController } from '../Scripts/Entities/PlayerController';
import { EnemyManager, BaseEnemy } from '../Scripts/Entities/EnemyManager';
import { LevelManager, LevelDataStructure } from '../Scripts/Systems/LevelManager';
import { CollectibleSystem } from '../Scripts/Systems/CollectibleSystem';
import { PortalSystem } from '../Scripts/Systems/PortalSystem';
import { CheckpointSystem } from '../Scripts/Systems/CheckpointSystem';
import { UIManager } from '../Scripts/Systems/UIManager';
import { CameraController } from '../Scripts/Systems/CameraController';
import { GameManager } from '../Scripts/Systems/GameManager';
import { PhysicsManager } from '../Scripts/Systems/PhysicsManager';
import { DiscordRichPresenceManager } from '../Scripts/Systems/DiscordRichPresenceManager';
import { AudioManager } from '../Scripts/Systems/AudioManager';

export class GameScene extends Phaser.Scene {
  private levelData!: LevelDataStructure;
  private player!: PlayerController;
  private levelManager!: LevelManager;
  private enemyManager!: EnemyManager;
  private collectibleSystem!: CollectibleSystem;
  private portalSystem!: PortalSystem;
  private checkpointSystem!: CheckpointSystem;
  private uiManager!: UIManager;
  private cameraController!: CameraController;
  private gameMgr!: GameManager;

  // Parallax background tiles
  private bgMountains!: Phaser.GameObjects.TileSprite;
  private bgForest!: Phaser.GameObjects.TileSprite;
  private bgIslands!: Phaser.GameObjects.TileSprite;

  // Portals and Checkpoints Static Groups
  private portalGroup!: Phaser.Physics.Arcade.StaticGroup;
  private checkpointGroup!: Phaser.Physics.Arcade.StaticGroup;

  // Ending Castle Portal
  private castlePortal!: Phaser.Physics.Arcade.Sprite;

  private customLevelData?: LevelDataStructure;

  constructor() {
    super('GameScene');
  }

  public init(data?: { customLevel?: LevelDataStructure }): void {
    if (data && data.customLevel) {
      this.customLevelData = data.customLevel;
    }
  }

  public create(): void {
    this.gameMgr = GameManager.getInstance();
    this.gameMgr.startTimer();

    DiscordRichPresenceManager.init();
    DiscordRichPresenceManager.updatePresence('Exploring Ancient Ruins', 'Level 1: Forgotten Ruins');

    // 1. Level & Systems Initialization
    this.levelManager = new LevelManager(this);
    this.levelData = this.levelManager.loadLevel(this.customLevelData);

    PhysicsManager.configureWorld(this, this.levelData.width, this.levelData.height);

    // 2. Parallax Backgrounds
    this.createParallaxBackgrounds();

    // 3. Player Spawn
    const spawnX = this.gameMgr.saveData.checkpointX || this.levelData.spawn.x;
    const spawnY = this.gameMgr.saveData.checkpointY || this.levelData.spawn.y;
    this.player = new PlayerController(this, spawnX, spawnY);
    this.player.setDepth(30);

    // 4. Enemy Manager Setup
    this.enemyManager = new EnemyManager(this);
    this.enemyManager.spawnEnemies(this.levelData.enemies);

    // 5. Collectibles Setup
    this.collectibleSystem = new CollectibleSystem(this);
    this.collectibleSystem.spawnCollectibles(
      this.levelData.collectibles.coins,
      this.levelData.collectibles.gems
    );

    // 6. Portals & Checkpoints Setup
    this.portalGroup = this.physics.add.staticGroup();
    this.portalSystem = new PortalSystem(this);
    this.portalSystem.createPortals(this.levelData.portals, this.portalGroup);

    this.checkpointGroup = this.physics.add.staticGroup();
    this.checkpointSystem = new CheckpointSystem(this);
    this.checkpointSystem.createCheckpoints(this.levelData.checkpoints, this.checkpointGroup);

    // 7. Castle & Exit Portal Setup
    this.createCastleStructure();

    // 8. NPCs Setup
    this.createNPCs();

    // 9. Camera Setup
    this.cameraController = new CameraController(this);
    this.cameraController.setupCamera(this.player, this.levelData.width, this.levelData.height);

    // 10. UI Manager Setup
    this.uiManager = new UIManager(this);

    // 11. Physics Collisions & Overlaps Setup
    this.setupCollisions();
  }

  private createParallaxBackgrounds(): void {
    const { width, height } = this.levelData;

    // Sunset Sky Gradient
    const sky = this.add.graphics();
    sky.fillGradientStyle(0x0f172a, 0x1e1b4b, 0x581c87, 0x7c2d12, 1);
    sky.fillRect(0, 0, width, height);
    sky.setDepth(-100);

    this.bgMountains = this.add.tileSprite(0, height - 256, width, 256, 'bg_mountains').setOrigin(0, 0).setScrollFactor(0.2).setDepth(-80);
    this.bgIslands = this.add.tileSprite(0, height - 320, width, 256, 'bg_islands').setOrigin(0, 0).setScrollFactor(0.4).setDepth(-60);
    this.bgForest = this.add.tileSprite(0, height - 200, width, 256, 'bg_forest').setOrigin(0, 0).setScrollFactor(0.6).setDepth(-40);
  }

  private createCastleStructure(): void {
    const castleData = this.levelData.castle;
    this.add.image(castleData.x, castleData.y, 'castle_structure').setDepth(5);

    // Exit portal frame at castle doorway
    this.castlePortal = this.physics.add.staticSprite(castleData.x, castleData.y + 40, 'portal_blue');
    this.castlePortal.setScale(1.2);
    this.castlePortal.setDepth(20);
    this.tweens.add({
      targets: this.castlePortal,
      alpha: 0.6,
      duration: 500,
      yoyo: true,
      repeat: -1,
    });
  }

  private createNPCs(): void {
    this.levelData.npcs.forEach((npcData) => {
      const npc = this.physics.add.staticSprite(npcData.x, npcData.y, 'npc');
      npc.setDepth(30);
      npc.setData('dialogue', npcData.dialogue);

      // Overlap with player triggers dialogue toast
      this.physics.add.overlap(this.player, npc, () => {
        this.uiManager.showDialogue(`[NPC]: ${npcData.dialogue}`);
      });
    });
  }

  private setupCollisions(): void {
    // Player vs Platforms & Hazards
    this.physics.add.collider(this.player, this.levelManager.staticPlatforms);
    this.physics.add.collider(this.player, this.levelManager.hazardGroup, () => {
      this.player.takeDamage(1);
      // Respawn at checkpoint if HP drops to 0
      if (this.player.hp <= 0) {
        this.respawnAtCheckpoint();
      }
    });

    // Player vs Moving Platforms
    this.levelManager.movingPlatforms.forEach((mp) => {
      this.physics.add.collider(this.player, mp);
    });

    // Player vs Bounce Flowers
    this.levelManager.bounceFlowers.forEach((flower) => {
      this.physics.add.overlap(this.player, flower, () => {
        flower.triggerBounce(this.player);
      });
    });

    // Player vs Switches & Doors
    this.levelManager.switches.forEach((sw) => {
      this.physics.add.overlap(this.player, sw, () => {
        sw.press((doorId) => {
          const door = this.levelManager.doors.get(doorId);
          if (door) {
            door.openDoor();
            this.uiManager.showToast('🔓 DOOR UNLOCKED!', 3000, '#4ade80');
          }
        });
      });
    });

    this.levelManager.doors.forEach((door) => {
      this.physics.add.collider(this.player, door, () => {
        if (!door.isOpen && (!this.player.getData('doorMsgTimer') || this.time.now > this.player.getData('doorMsgTimer'))) {
          this.player.setData('doorMsgTimer', this.time.now + 3000);
          this.uiManager.showToast('🔒 DOOR LOCKED! Locate the pressure switch nearby.', 3000, '#f87171');
        }
      });
    });

    // Player vs Vines
    this.physics.add.overlap(this.player, this.levelManager.vinesGroup, () => {
      this.player.touchingVineThisFrame = true;
      this.player.isClimbing = true;
    });

    // Player vs Enemies
    this.physics.add.overlap(this.player, this.enemyManager.group, (pObj, eObj) => {
      this.enemyManager.handlePlayerCollision(this.player, eObj as BaseEnemy);
      if (this.player.hp <= 0) {
        this.respawnAtCheckpoint();
      }
    });

    // Enemies vs Platforms
    this.physics.add.collider(this.enemyManager.group, this.levelManager.staticPlatforms);

    // Player vs Collectibles
    this.physics.add.overlap(this.player, this.collectibleSystem.coinGroup, (_player, coin) => {
      this.collectibleSystem.collectCoin(this.player, coin as Phaser.GameObjects.GameObject);
    });

    this.physics.add.overlap(this.player, this.collectibleSystem.gemGroup, (_player, gem) => {
      this.collectibleSystem.collectGem(this.player, gem as Phaser.GameObjects.GameObject);
    });

    // Player vs Portals
    this.physics.add.overlap(this.player, this.portalGroup, (_player, portal) => {
      this.portalSystem.handlePlayerPortalOverlap(this.player, portal as Phaser.GameObjects.GameObject);
    });

    // Player vs Checkpoints
    this.physics.add.overlap(this.player, this.checkpointGroup, (_player, cp) => {
      this.checkpointSystem.handlePlayerOverlap(this.player, cp as Phaser.GameObjects.GameObject);
    });

    // Player vs Castle Exit Portal (Victory)
    this.physics.add.overlap(this.player, this.castlePortal, () => {
      if (this.gameMgr.isLevelComplete) return;

      AudioManager.playSFX('victory');
      this.gameMgr.completeLevel(this.collectibleSystem.coinsCollected, this.collectibleSystem.gemsCollected);

      const mins = Math.floor(this.gameMgr.playTimeSeconds / 60);
      const secs = Math.floor(this.gameMgr.playTimeSeconds % 60);
      const timeStr = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

      this.uiManager.showVictoryScreen(
        this.collectibleSystem.coinsCollected,
        this.collectibleSystem.gemsCollected,
        timeStr,
        () => {
          this.gameMgr.resetSession();
          this.scene.restart();
        }
      );
    });

    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-F1', () => {
        this.scene.start('LevelEditorScene', { level: this.levelData });
      });
    }
  }

  private respawnAtCheckpoint(): void {
    AudioManager.playSFX('enemy_hit');
    this.cameraController.shake(300, 0.02);

    const cpX = this.gameMgr.saveData.checkpointX || this.levelData.spawn.x;
    const cpY = this.gameMgr.saveData.checkpointY || this.levelData.spawn.y;

    this.player.setPosition(cpX, cpY);
    this.player.hp = this.player.maxHp;
    if (this.player.body) {
      const body = this.player.body as Phaser.Physics.Arcade.Body;
      body.setVelocity(0, 0);
    }
  }

  public update(time: number, delta: number): void {
    if (this.gameMgr.isLevelComplete) return;

    // 1. Update Player
    this.player.updatePlayer(delta);

    // 2. Update Level & Interactive Objects
    this.levelManager.updateLevel(delta);

    // 3. Update Enemy AI
    this.enemyManager.update(time, delta);

    // 4. Update Parallax Background Positions
    const camX = this.cameras.main.scrollX;
    this.bgMountains.tilePositionX = camX * 0.2;
    this.bgIslands.tilePositionX = camX * 0.4;
    this.bgForest.tilePositionX = camX * 0.6;

    // 5. Update HUD
    this.uiManager.updateHUD(
      this.player.hp,
      this.player.maxHp,
      this.collectibleSystem.coinsCollected,
      this.collectibleSystem.gemsCollected,
      this.gameMgr.playTimeSeconds
    );
  }
}
